import React from "react";
import { Button, Card, Container } from "../../components/atoms";

// ─── Tiny reusable pieces — all using exact Solveesy palette ─────────────────

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-block bg-[#EEF4FF] text-[#015CFD] text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
    {children}
  </span>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[11px] font-bold tracking-[2px] uppercase text-[#686868] mb-1">
    {children}
  </p>
);

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-[#000000] font-bold text-[22px] md:text-[28px] mt-12 mb-4 pb-3 border-b-2 border-[#000000] leading-snug">
    {children}
  </h2>
);

const Body = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <p className={`text-[15px] text-[#464646] leading-[1.78] mb-5 font-normal ${className}`}>
    {children}
  </p>
);

const PullQuote = ({ children }: { children: React.ReactNode }) => (
  <div className="border-l-4 border-[#015CFD] bg-[#EEF4FF] rounded-r-lg px-6 py-5 my-8">
    <p className="text-[#015CFD] text-[16px] md:text-[18px] italic leading-[1.6] m-0 font-semibold">
      {children}
    </p>
  </div>
);

const InfographicCard = ({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="my-8 border-[#E3E4F1]">
    <SectionLabel>{label}</SectionLabel>
    <h3 className="text-[#000000] font-bold text-[18px] md:text-[22px] mb-6 leading-snug">
      {title}
    </h3>
    {children}
  </Card>
);

// ─── INFOGRAPHIC 1 — Before vs After ─────────────────────────────────────────
const BeforeAfter = () => {
  const before = [
    <>Rollovers allowed <strong className="text-[#101010]">up to 2× without classification</strong> — lenders could "press snooze" indefinitely on stressed accounts</>,
    <>5% provisioning buffer on restructured standard assets — cost that <strong className="text-[#101010]">discouraged honest classification</strong></>,
    <>NPA recovery provisioning spread evenly — <strong className="text-[#101010]">no front-loading penalty</strong> for late action</>,
    <>Borrowers quietly rolled over — <strong className="text-[#101010]">credit score unaffected</strong></>,
  ];
  const after = [
    <>Rollover exemption <strong className="text-[#015CFD]">deleted</strong> — any extension immediately triggers restructuring scrutiny from Day 1</>,
    <>5% buffer removed — RBI signal: <strong className="text-[#015CFD]">"no excuse not to classify honestly"</strong></>,
    <>NPA upgrade provisioning <strong className="text-[#015CFD]">front-loaded into Year 1</strong> — recovery is now the expensive moment</>,
    <>Formal restructuring <strong className="text-[#015CFD]">reported to credit bureaus</strong> — permanent damage to borrower profile</>,
  ];

  const Col = ({
    items,
    variant,
  }: {
    items: React.ReactNode[];
    variant: "before" | "after";
  }) => (
    <div
      className={`rounded-lg p-5 ${
        variant === "before"
          ? "bg-[#F6F6F6] border border-[#E3E4F1]"
          : "bg-[#EEF4FF] border border-[#015CFD]/25"
      }`}
    >
      <div
        className={`text-[11px] font-bold tracking-[1.5px] uppercase mb-4 ${
          variant === "before" ? "text-[#686868]" : "text-[#015CFD]"
        }`}
      >
        {variant === "before" ? "⏱ Before Nov 2025" : "⚡ After Nov 2025"}
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 mb-3 text-[13.5px] text-[#464646] leading-snug">
          <div
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-[6px] ${
              variant === "before" ? "bg-[#686868]" : "bg-[#015CFD]"
            }`}
          />
          <span>{item}</span>
        </div>
      ))}
    </div>
  );

  return (
    <InfographicCard label="Regulatory Shift · Nov 2025" title="Two Key Changes That Collapsed the Collections Window">
      {/* Desktop */}
      <div className="hidden md:grid grid-cols-[1fr_44px_1fr] items-start gap-0">
        <Col items={before} variant="before" />
        <div className="flex items-center justify-center pt-10">
          <div className="w-7 h-7 rounded-full bg-[#EEF4FF] border border-[#015CFD]/30 flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7h8M9 5l2 2-2 2" stroke="#015CFD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <Col items={after} variant="after" />
      </div>
      {/* Mobile */}
      <div className="md:hidden flex flex-col gap-4">
        <Col items={before} variant="before" />
        <Col items={after} variant="after" />
      </div>
    </InfographicCard>
  );
};

// ─── INFOGRAPHIC 2 — Borrower Cascade ────────────────────────────────────────
const BorrowerCascade = () => {
  const steps = [
    {
      symbol: "✓",
      dot: "bg-[#E8F5EF] border-[#15764A] text-[#15764A]",
      tagColor: "text-[#15764A]",
      tag: "30–45 DPD · The Intervention Window",
      title: "Structured repayment arrangement reached",
      desc: (
        <>
          Before formal classification. Borrower credit history{" "}
          <strong className="text-[#15764A]">stays intact</strong>. Lender avoids restructuring
          notation. Both parties benefit. This is the optimal intervention window.
        </>
      ),
      last: false,
    },
    {
      symbol: "!",
      dot: "bg-[#FEF9EC] border-[#B45309] text-[#B45309]",
      tagColor: "text-[#92400E]",
      tag: "60–90 DPD · Formal Classification Trigger",
      title: "Account classified as Restructured",
      desc: (
        <>
          Borrower's credit bureau profile marked "Restructured." Future borrowing capacity from{" "}
          <em>every</em> lender{" "}
          <strong className="text-[#B07010]">is now impacted</strong>. Lender faces provisioning
          obligations with no buffer.
        </>
      ),
      last: false,
    },
    {
      symbol: "✕",
      dot: "bg-[#FEF2F2] border-[#B91C1C] text-[#B91C1C]",
      tagColor: "text-[#B91C1C]",
      tag: "90+ DPD · NPA Classification",
      title: "Full regulatory cascade begins",
      desc: (
        <>
          NPA designation. Mandatory credit bureau reporting. For ₹25L+ accounts: monthly CIC
          reporting, potential criminal complaints. Recovery triggers{" "}
          <strong className="text-[#B91C1C]">front-loaded Year 1 provisioning</strong> cost.
        </>
      ),
      last: false,
    },
    {
      symbol: "⚠",
      dot: "bg-[#FEF2F2] border-[#B91C1C] text-[#B91C1C]",
      tagColor: "text-[#B91C1C]",
      tag: "Wilful Default · ₹25 Lakh+ Outstanding",
      title: "Directors debarred; criminal complaints mandatory",
      desc: (
        <>
          Directors and promoters debarred from any bank-borrowing entity directorship. NBFC CEO
          faces <strong className="text-[#B91C1C]">personal liability</strong> for agent conduct
          under the Responsible Business Conduct Directions.
        </>
      ),
      last: true,
    },
  ];

  return (
    <InfographicCard label="Borrower Impact · Under New Framework" title="What Happens When a Borrower Drifts — Stage by Stage">
      <div className="flex flex-col">
        {steps.map((s, i) => (
          <div key={i} className="flex gap-4 relative">
            {!s.last && (
              <div className="absolute left-[18px] top-[40px] bottom-0 w-[2px] bg-[#E3E4F1]" />
            )}
            <div
              className={`w-[38px] h-[38px] min-w-[38px] rounded-full flex items-center justify-center font-bold text-[13px] z-10 border-2 ${s.dot}`}
            >
              {s.symbol}
            </div>
            <div className="pb-7 pt-1">
              <div className={`text-[11px] font-bold tracking-[1px] uppercase mb-1 ${s.tagColor}`}>
                {s.tag}
              </div>
              <div className="text-[#101010] font-semibold text-[15px] mb-1">{s.title}</div>
              <div className="text-[13.5px] text-[#464646] leading-[1.55]">{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </InfographicCard>
  );
};

// ─── INFOGRAPHIC 3 — What Solveesy Does Differently ──────────────────────────
// Uses the exact Solveesy pink→purple gradient card (from Contact.tsx / Datadriven.tsx)
const SolveesyDiff = () => {
  const items = [
    {
      icon: "🎯",
      title: "Early Intervention at 30–60 DPD",
      desc: "We engage before NPA territory — not after 365 days. Early stress signals identified and acted on proactively.",
    },
    {
      icon: "📊",
      title: "Data-Informed Repayment Structures",
      desc: "Structures designed around the borrower's actual cash flow reality — not a generic template.",
    },
    {
      icon: "🔍",
      title: "Full Audit Trails",
      desc: "Complete visibility for the lender into every borrower interaction. Built for RBI inspection readiness from day one.",
    },
    {
      icon: "⚖️",
      title: "Compliance-First Conduct",
      desc: "Registered service provider status. Standards that protect the NBFC from the CEO liability provisions in the new Directions.",
    },
  ];

  return (
 <Card className="my-8 bg-gradient-to-br from-[#EEF4FF] to-[#F8FAFF]  border-l-4 !border-[#015CFD] rounded-2xl px-6 py-6">

  {/* Gradient Glow Layer */}
  <div className="absolute inset-0 
    bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.25),transparent_60%)]
    pointer-events-none" 
  />

  <div className="relative px-6 md:px-10 py-8 md:py-10">

    <p className="text-[11px] font-bold tracking-[2px] uppercase text-white/70 mb-1">
      Solveesy · Collections as a Service
    </p>

    <h3 className="text-white font-bold text-[20px] md:text-[24px] mb-6 leading-snug">
      Built for the 30–60 DPD Window from Day One
    </h3>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      {items.map((item, i) => (
        <div
          key={i}
          className="bg-white/15 backdrop-blur-md border border-white/25 rounded-xl p-5"
        >
          <div className="text-[22px] mb-2">{item.icon}</div>

          <div className="text-white font-semibold text-[14px] mb-1">
            {item.title}
          </div>

          <div className="text-white/80 text-[13px] leading-snug font-normal">
            {item.desc}
          </div>
        </div>
      ))}
    </div>

    <div className="bg-white/15 backdrop-blur-md border border-white/25 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
      <span className="text-white/85 text-[13.5px] font-normal">
        The regulatory shift has moved the intervention point from{" "}
        <strong className="text-white">365+ DPD</strong> to{" "}
        <strong className="text-[#015CFD]">30–60 DPD</strong>. Solveesy has been built for
        that window from day one.
      </span>
    </div>

  </div>
</Card>
  );
};

// ─── INFOGRAPHIC 4 — 3 Questions ─────────────────────────────────────────────
const ThreeQuestions = () => {
  const qs = [
    {
      q: "Which accounts have been informally extended in the last 24 months?",
      detail:
        "Under the new framework, those extensions are no longer available. When borrowers return for another extension, do you have the infrastructure to manage the volume if many surface simultaneously?",
    },
    {
      q: "When a borrower hits 30 DPD today, what actually happens in your organisation?",
      detail:
        'Is there a structured, compliance-ready engagement — or is the answer "a reminder SMS and then it goes to the call centre queue"?',
    },
    {
      q: "Who is accountable for the conduct of collections agents working on your behalf?",
      detail:
        "Can you demonstrate to an RBI inspector that you have full oversight of what those agents are saying to your borrowers?",
    },
  ];

  return (
    <InfographicCard
      label="Self-Assessment · For Collections Heads"
      title="Three Questions Every NBFC Should Answer This Quarter"
    >
      <div className="flex flex-col gap-4">
        {qs.map((item, i) => (
          <div
            key={i}
            className="flex gap-4 bg-[#EEF4FF] rounded-xl px-5 py-5 border-l-4 border-[#015CFD]"
          >
            <div className="text-[36px] font-bold text-[#015CFD] leading-none min-w-[24px] pt-1">
              {i + 1}
            </div>
            <div>
              <div className="text-[15px] font-semibold text-[#101010] mb-1 leading-snug">
                {item.q}
              </div>
              <p className="text-[13.5px] text-[#464646] leading-[1.55] m-0 font-normal">
                {item.detail}
              </p>
            </div>
          </div>
        ))}
      </div>
    </InfographicCard>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const SolveesyBlog = () => {
  return (
    <div className="bg-white min-h-screen">

      {/* ── HERO — white bg, exact Solveesy hero layout ── */}
      <div className="relative overflow-hidden">
        {/* Same decorative blur used on every Solveesy page */}
        <div className="w-[403px] h-[403px] rounded-full bg-[#00C2FF] blur-[200px] absolute -z-10 opacity-25 -top-20 left-[60px] max-sm:hidden pointer-events-none" />

        <Container className="py-14 md:py-20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-10">

            {/* Left */}
            <div className="w-full md:w-full flex flex-col items-center md:items-start text-center md:text-left">
              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                <Tag>RBI 2025</Tag>
                <Tag>Master Directions</Tag>
                <Tag>NBFCs</Tag>
              </div>

              <h1 className="text-[28px] md:text-[46px] font-bold leading-tight text-[#000000] mb-3">
                The Clock Has Moved Forward.{" "}
                <br/>
                <span className="text-[#015CFD] md:text-[30px]">Your Collections Strategy Hasn't.</span>
              </h1>

              <p className="text-[#464646] text-sm md:text-base font-normal w-full md:w-4/5 leading-relaxed mb-6">
                Why RBI's 2025 Master Directions make early-stage collections the most important
                conversation in lending right now — for lenders and borrowers alike.
              </p>
            </div>
          </div>
        </Container>
      </div>

      {/* ── ARTICLE BODY ── */}
      <Container className=" py-10 md:py-14">

        {/* Intro */}
         <p className="text-[15px] md:text-[16px] text-[#464646] leading-[1.78] border-l-4 border-[#015CFD] pl-5 mb-6 font-normal">
          There is a moment in every lending relationship when something quietly breaks. The borrower
          misses an EMI. Then another. The lender's system flags it. A reminder goes out. Maybe a
          call. Maybe not — because the internal team is stretched, the account is small, and
          honestly, the real muscle only gets deployed once the account crosses 365 days past due.
        </p>

        <Body>
          That is how the Indian lending market has worked for years. Early delinquency gets managed
          with SMS reminders and light nudges. The serious collections infrastructure — the specialist
          agencies, the legal muscle, the data-driven negotiation playbooks — gets deployed late,
          very late, often when the borrower's situation has deteriorated beyond easy resolution.
        </Body>

        <Body>
          <strong className="text-[#000000]">
            The RBI's 2025 Master Directions have quietly made that model obsolete.
          </strong>
        </Body>

        {/* ─── Section 1 ─── */}
        <H2>What Changed, and Why It Matters More Than People Realise</H2>

        <Body>
          In November 2025, the RBI issued 35 standalone Master Directions for NBFCs, restructuring
          decades of regulation. Two specific changes, read together, fundamentally alter the
          economics of when a lender must act on a stressed account.
        </Body>

        <BeforeAfter />

        {/* ─── Section 2 ─── */}
        <H2>The 365-DPD Strategy Is Now a Liability</H2>

        <Body>
          For years, the implicit industry standard has been to let the internal team handle early
          delinquency in-house with standard tools — automated reminders, basic call centre outreach
          — and bring in specialised collections support only after an account has been overdue for a
          year or more.
        </Body>

        <Body>
          The logic made sense when rollovers bought time, when restructured standard assets carried
          a provisioning cost that made lenders reluctant to classify, and when the regulatory
          framework broadly tolerated a period of quiet extend-and-pretend.
        </Body>

        <Body>None of that logic holds anymore.</Body>

        <Body>
          Under the new framework, an account at 30 DPD is not a mild compliance problem. It is the
          beginning of a compressed timeline that — if not interrupted — leads to formal
          restructuring classification, NPA designation, and eventually the front-loaded provisioning
          hit on recovery. Every day an account sits in early delinquency without effective
          intervention is a day the cost of resolution goes up.
        </Body>

        <PullQuote>
          The lender who deploys sophisticated collections capability at 30 DPD is operating in a
          fundamentally different risk universe than the lender who waits for 365 DPD and then
          outsources.
        </PullQuote>

        {/* ─── Section 3 ─── */}
        <H2>The Borrower Problem That No One Is Talking About</H2>

        <Body>
          Under the new Wilful Defaulters Direction, NBFCs are now required to report wilful
          defaulters — with outstanding amounts above ₹25 lakh — to credit information companies on
          a monthly basis. Directors and promoters of companies classified as wilful defaulters are
          debarred from directorship of any bank-borrowing entity. Criminal complaints become
          mandatory.
        </Body>

        <Body>
          Under the Responsible Business Conduct Directions, any borrower whose account goes through
          formal restructuring is now reported to credit bureaus as "Restructured" — an event that
          damages their credit profile in ways that were previously easier to avoid.
        </Body>

        <BorrowerCascade />

        {/* ─── Section 4 ─── */}
        <H2>
          Why Lenders Cannot Do This Alone — And Why the Old Outsourcing Model Doesn't Fit Either
        </H2>

        <Body>
          The RBI has explicitly placed the responsibility for customer grievances arising from
          outsourced collections activity — including that of sub-agents — squarely on the NBFC. Not
          on the agency. <strong className="text-[#000000]">On the NBFC.</strong> And the
          Responsible Business Conduct Directions add personal liability on the CEO for collection
          harassment carried out by any Lending Service Provider working on the NBFC's behalf.
        </Body>

        <Body>
          This means the old model — outsource the collections problem to an agency at 365 DPD,
          write them a contract, and step away — carries personal liability risk for the NBFC's
          leadership that did not exist before. Every collections partner is now an extension of the
          NBFC's compliance posture.
        </Body>

        <PullQuote>
          It cannot be a volume-based call centre grinding through a list. It needs to be a
          structured, compliant, data-driven service where the lender has full visibility into what
          is being said to their borrowers, how, and with what outcomes.
        </PullQuote>

        {/* ─── Section 5 ─── */}
        <H2>What Solveesy Does Differently</H2>
        <SolveesyDiff />

        {/* ─── Section 6 ─── */}
        <H2>The Conversation Every Lender Should Be Having</H2>

        <Body>
          If you are running collections at an NBFC, an HFC, or a digital lending platform, there
          are three questions worth sitting with this month:
        </Body>

        <ThreeQuestions />

        <Body>
          The 2025 Directions have not made lending harder. They have made{" "}
          <strong className="text-[#000000]">
            late action more expensive, more risky, and less effective.
          </strong>
        </Body>

        <Body>
          The lenders who figure that out in Q1 2026 will have a structural advantage over those who
          figure it out in Q4.
        </Body>

        {/* ── CTA — exact gradient card from Contact.tsx / Datadriven.tsx ── */}
  <Card className="relative  rounded-[40px] mt-14 overflow-hidden">

  {/* Glow highlight */}
  <div className="absolute inset-0 bg-gradient-to-br from-[#EEF4FF] to-[#F8FAFF]  border-l-4 !border-[#015CFD] pointer-events-none" />

  <div className="relative flex flex-col items-center justify-center text-center px-6 md:px-16 py-10 md:py-14">

    <h2 className="text-white font-bold text-[24px] md:text-[36px] leading-tight mb-3">
      Rethinking your collections strategy?
    </h2>

    <p className="text-white/80 text-[15px] md:text-[18px] leading-relaxed max-w-lg mb-3 font-normal">
      Solveesy works with lenders across consumer finance, digital lending, and MSME credit
      to build compliant, early-stage collections capability in light of the 2025 RBI Master
      Directions.
    </p>

    <p className="text-white font-semibold text-[16px] md:text-[20px] mb-8">
      Let's redefine debt recovery together. Get Started Today.
    </p>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button
        type="button"
        className="text-[#015CFD] bg-white hover:bg-[#F0F5FF] text-[16px] font-medium px-6 py-2"
      >
        Connect with us →
      </Button>
    </div>
  </div>
</Card>
      </Container>
    </div>
  );
};

export default SolveesyBlog;